import React, { ClipboardEvent, useEffect, useRef, useState, DragEvent } from 'react';
import '@blueprintjs/table/lib/css/table.css';
import '@blueprintjs/popover2/lib/css/blueprint-popover2.css';
import { FieldProps } from 'formik';
import { HotkeysProvider } from '@blueprintjs/core';
import clsx from 'clsx';
import { CellProps, Column, FocusedCellCoordinates, Region, Table2 } from '@blueprintjs/table';
import TextAreaCell from '../SequenceInput/TextAreaCell';
import { resizeRowsByApproximateHeight } from '@blueprintjs/table/lib/esnext/resizeRows';
import ResizableDiv from '../SequenceInput/ResizableDiv';
import PasteModal, { PasteType } from '../SequenceInput/PasteModal';
import { IMultiChainCompoundValue } from '../types';
import { ISetting } from '@biolibtech/biolib-js';
import ChainTypeCell from './ChainTypeCell';
import { parseFasta } from '../SequenceInput/validation';
import { detectChainType, validateAllRows } from './validation';
import { useScrollbarWidth } from '../utils';

interface IProps extends FieldProps {
    autoFocus: boolean;
    setting: ISetting;
}

export default function MultiChainCompoundInput(props: IProps) {
    const { rows, filename } = props.field.value as IMultiChainCompoundValue;

    const tableRef = useRef<Table2>();
    const containerRef = useRef<HTMLDivElement>();
    const [containerWidth, setContainerWidth] = useState(null);
    const [tableRenderInvalidator, setTableRenderInvalidator] = useState<boolean>(false);
    const [enableFocusedCell, setEnableFocusedCell] = useState<boolean>(props.autoFocus);
    const [focusedCell, setFocusedCell] = useState<FocusedCellCoordinates>({ row: 0, col: 1, focusSelectionIndex: 0 });
    const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);
    const scrollbarWidth = useScrollbarWidth() + 5;

    const columnWidths = [120, (containerWidth ?? 500) - 120 - 150 - scrollbarWidth, 150];
    const [rowHeights, setRowHeights] = useState<number[]>([]);
    const [pastedTextForModal, setPastedTextForModal] = useState<string | null>(null);
    const [temporaryError, setTemporaryError] = useState<string | null>(null);

    const validateAndSetErrors = () => {
        if (temporaryError) {
            props.form.setFieldError(props.field.name, temporaryError);
            return;
        }
        
        const errorMessage = validateAllRows(rows, props.setting);
        if (errorMessage) {
            props.form.setFieldError(props.field.name, errorMessage);
        } else {
            props.form.setFieldError(props.field.name, undefined);
        }
    };

    useEffect(() => {
        validateAndSetErrors();
    }, [rows, temporaryError]);

    useEffect(() => {
        const rowHeights: number[] = resizeRowsByApproximateHeight(
            Object.keys(rows).length,
            columnWidths,
            (rowIndex: number, columnIndex: number) => rows[rowIndex][columnIndex] || '-',
            {
                getApproximateCharWidth: 7.5,
                getApproximateLineHeight: 20,
                getCellHorizontalPadding: 7,
                getNumBufferLines: 0,
            },
        );
        for (let i = 0; i < rowHeights.length; i += 1) {
            rowHeights[i] += 2;
        }
        setRowHeights(rowHeights);
        setTableRenderInvalidator(!tableRenderInvalidator);
    }, [containerWidth, rows]);

    const columns: string[] = ['Chain Name', 'Sequence', 'Chain Type'];

    const updateRows = (rowsToUpdate: Record<number, Record<number, string>>, forceTableReRender: boolean) => {
        const newRows = { ...rows, ...rowsToUpdate };
        const newRowsLength = Object.keys(newRows).length;
        const lastRow = newRows[newRowsLength - 1];
        if (lastRow[0] || lastRow[1] || lastRow[2]) {
            newRows[newRowsLength] = { 0: '', 1: '', 2: '' };
        }
        props.form.setFieldTouched(props.field.name, true, false);
        props.form.setFieldValue(props.field.name, { filename, rows: newRows }, true);
        if (forceTableReRender) {
            setTableRenderInvalidator(!tableRenderInvalidator);
        }
    };

    useEffect(() => {
        const onResize = () => {
            if (containerRef.current) {
                setContainerWidth(Math.max(containerRef.current.offsetWidth, 100));
            }
        };
        onResize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    const cellRenderer = (rowIndex: number, columnIndex: number): React.ReactElement<CellProps> => {
        if (columnIndex === 2) {
            return (
                <ChainTypeCell
                    value={rows[rowIndex]?.[columnIndex] ?? ''}
                    setValue={(newValue) => {
                        updateRows({ [rowIndex]: { ...rows[rowIndex], [columnIndex]: newValue } }, false);
                    }}
                    readOnly={false}
                    sequence={rows[rowIndex]?.[1] ?? ''}
                    onValidationError={(error) => {
                        if (error) {
                            setTemporaryError(error);
                        } else {
                            setTemporaryError(null);
                        }
                    }}
                />
            );
        }

        return (
            <TextAreaCell
                value={rows[rowIndex]?.[columnIndex] ?? ''}
                onFocus={() => {
                    if (
                        rowIndex === 0 &&
                        rowIndex === focusedCell.row &&
                        columnIndex === 1 &&
                        columnIndex === focusedCell.col
                    ) {
                        // initial focus do nothing
                    } else {
                        props.form.setFieldTouched(props.field.name, true, true);
                    }
                }}
                setValue={(newValue) => {
                    const currentRow = rows[rowIndex] || {};
                    const updatedRow = { ...currentRow, [columnIndex]: newValue };

                    if (columnIndex === 1 && newValue.trim()) {
                        const detectedType = detectChainType(newValue.trim());
                        if (!currentRow[2]) {
                            updatedRow[2] = detectedType;
                        }
                    }

                    updateRows({ [rowIndex]: updatedRow }, false);
                    setTemporaryError(null);
                }}
                readOnly={false}
                placeholder={
                    rowIndex !== 0 ? undefined : columnIndex === 0 ? 'A' : '[Write sequence or paste FASTA here]'
                }
            />
        );
    };

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>): void => {
        const rawText = event.clipboardData.getData('Text').trim();
        if (!focusedCell) {
            return;
        }

        let isPastingFromSpreadsheet = true;
        for (const line of rawText.split('\n')) {
            if (!line.includes('\t')) {
                isPastingFromSpreadsheet = false;
                break;
            }
        }

        if (isPastingFromSpreadsheet) {
            const pastedRows = rawText.split('\n');
            const rowsToUpdate: Record<number, Record<number, string>> = {};
            for (let i = 0; i < pastedRows.length; i += 1) {
                const pastedColumns = pastedRows[i].trim().split('\t');

                const newRowIndex = focusedCell.row + i;
                const currentRow = newRowIndex in rows ? { ...rows[newRowIndex] } : {};
                rowsToUpdate[newRowIndex] = currentRow;
                
                if (focusedCell.col === 0) {
                    rowsToUpdate[newRowIndex][0] = (pastedColumns[0] ?? '').replace(/\s+/g, '').replace('>', '');
                    if (pastedColumns.length > 1) {
                        const sequence = (pastedColumns[1] ?? '').replace(/\s+/g, '');
                        rowsToUpdate[newRowIndex][1] = sequence;
                        if (sequence.trim() && !currentRow[2]) {
                            const detectedType = detectChainType(sequence.trim());
                            rowsToUpdate[newRowIndex][2] = detectedType;
                        }
                    }
                    if (pastedColumns.length > 2) {
                        rowsToUpdate[newRowIndex][2] = (pastedColumns[2] ?? '').trim();
                    }
                } else if (focusedCell.col === 1) {
                    const sequence = (pastedColumns[0] ?? '').replace(/\s+/g, '');
                    rowsToUpdate[newRowIndex][1] = sequence;
                    if (sequence.trim() && !currentRow[2]) {
                        const detectedType = detectChainType(sequence.trim());
                        rowsToUpdate[newRowIndex][2] = detectedType;
                    }
                } else if (focusedCell.col === 2) {
                    rowsToUpdate[newRowIndex][2] = (pastedColumns[0] ?? '').trim();
                }
            }
            event.preventDefault();
            updateRows(rowsToUpdate, true);
            setTemporaryError(null);
        } else if (rawText.startsWith('>') && rawText.includes('\n')) {
            const rowsToUpdate: Record<number, Record<number, string>> = {};
            try {
                const records = parseFasta(rawText, props.setting.config, false);
                for (let i = 0; i < records.length; i += 1) {
                    const newRowIndex = focusedCell.row + i;
                    const record = records[i];
                    const detectedType = detectChainType(record.sequence);
                    rowsToUpdate[newRowIndex] = {
                        0: record.sequenceId,
                        1: record.sequence,
                        2: detectedType,
                    };
                }
                event.preventDefault();
                updateRows(rowsToUpdate, true);
                setTemporaryError(null);
            } catch (error) {
                setTemporaryError(`Failed to parse pasted text as FASTA. ${error.message}`);
            }
        } else if (rawText.includes('\n')) {
            event.preventDefault();
            event.stopPropagation();
            setPastedTextForModal(rawText);
        } else {
            event.preventDefault();
            event.stopPropagation();
            const value = focusedCell.col === 0 ? rawText.trim().replace('>', '') : rawText.trim();
            const rowsToUpdate: Record<number, Record<number, string>> = {
                [focusedCell.row]: { ...rows[focusedCell.row], [focusedCell.col]: value },
            };
            updateRows(rowsToUpdate, true);
            setTemporaryError(null);
        }
    };

    const deleteSelectedRegions = () => {
        const rowsToUpdate: Record<number, Record<number, string>> = {};
        for (const region of selectedRegions) {
            const {
                rows: [rowStart, rowEnd],
                cols: [colStart, colEnd],
            } = region;
            for (let i = rowStart; i < rowEnd + 1; i += 1) {
                rowsToUpdate[i] = rows[i];
                for (let j = colStart; j < colEnd + 1; j += 1) {
                    rowsToUpdate[i][j] = '';
                }
            }
        }
        updateRows(rowsToUpdate, true);
        setTemporaryError(null);
    };

    return (
        <div className='flex flex-col w-full space-y-2'>
            <PasteModal
                isOpen={!!pastedTextForModal}
                columnName={focusedCell.col === 0 ? 'chain name' : focusedCell.col === 1 ? 'sequence' : 'chain type'}
                onClose={(pasteType) => {
                    if (pasteType === PasteType.multiple) {
                        const pastedRows = pastedTextForModal.split('\n');
                        const rowsToUpdate: Record<number, Record<number, string>> = {};
                        for (let i = 0; i < pastedRows.length; i += 1) {
                            const newRowIndex = focusedCell.row + i;
                            rowsToUpdate[newRowIndex] = newRowIndex in rows ? { ...rows[newRowIndex] } : {};
                            rowsToUpdate[newRowIndex][focusedCell.col] = pastedRows[i].replace(/\s+/g, '');
                        }
                        updateRows(rowsToUpdate, true);
                        setTemporaryError(null);
                    } else if (pasteType === PasteType.single) {
                        const newRowIndex = focusedCell.row;
                        const rowsToUpdate: Record<number, Record<number, string>> = {};
                        rowsToUpdate[newRowIndex] = newRowIndex in rows ? { ...rows[newRowIndex] } : {};
                        rowsToUpdate[newRowIndex][focusedCell.col] = pastedTextForModal.replace(/\s+/g, '');
                        updateRows(rowsToUpdate, true);
                        setTemporaryError(null);
                    }
                    setPastedTextForModal(null);
                }}
            />
            <div
                className={clsx('w-full rounded-md overflow-hidden border relative', 'border-gray-300')}
                onPasteCapture={handlePaste}
                ref={containerRef}
                onMouseEnter={() => {
                    document.documentElement.style.overscrollBehaviorX = 'none';
                    setEnableFocusedCell(true);
                }}
                onMouseLeave={() => (document.documentElement.style.overscrollBehaviorX = '')}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Backspace') {
                        if (selectedRegions.length === 1) {
                            const { rows, cols } = selectedRegions[0];
                            const rowCount = rows[1] - rows[0];
                            const colCount = cols[1] - cols[0];
                            if (rowCount > 0 || colCount > 0) {
                                deleteSelectedRegions();
                            }
                        } else if (selectedRegions.length > 1) {
                            deleteSelectedRegions();
                        }
                    } else if (event.key === 'Delete') {
                        deleteSelectedRegions();
                    }
                }}
            >
                <ResizableDiv>
                    <HotkeysProvider>
                        <Table2
                            cellRendererDependencies={[tableRenderInvalidator]}
                            columnWidths={columnWidths}
                            enableColumnHeader={true}
                            enableColumnResizing={false}
                            enableFocusedCell={enableFocusedCell}
                            enableRowHeader={false}
                            focusedCell={focusedCell}
                            getCellClipboardData={(row: number, col: number) => rows[row]?.[col] ?? ''}
                            key={!containerWidth ? 'loading' : 'loaded'}
                            numRows={rowHeights.length}
                            onFocusedCell={setFocusedCell}
                            onSelection={(selectedRegions) => setSelectedRegions(selectedRegions)}
                            ref={tableRef}
                            rowHeights={rowHeights}
                            selectedRegions={selectedRegions}
                        >
                            {columns.map((name, index) => (
                                <Column
                                    key={index}
                                    cellRenderer={cellRenderer}
                                    nameRenderer={() => (
                                        <span
                                            style={{ marginLeft: -2, fontSize: 12 }}
                                            className='text-gray-600 font-bold'
                                        >
                                            {name}
                                        </span>
                                    )}
                                />
                            ))}
                        </Table2>
                    </HotkeysProvider>
                </ResizableDiv>
            </div>
        </div>
    );
}
